<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import BillingAPI from 'dashboard/api/billing';
import ButtonV4 from 'next/button/Button.vue';

const emit = defineEmits(['updated']);
const { t } = useI18n();
const isLoading = ref(false);
const isSaving = ref(false);
const asaasRequired = ref(false);
const isExpanded = ref(false);

// Form data
const formData = reactive({
  cpfCnpj: '',
  billingName: '',
  billingEmail: '',
  billingPhone: '',
  postalCode: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
});

// Original data for change detection
const originalData = ref({});

const formattedCpfCnpj = computed({
  get() {
    const value = formData.cpfCnpj || '';
    const digits = value.replace(/\D/g, '');

    if (digits.length <= 11) {
      // CPF: 000.000.000-00
      return digits
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    // CNPJ: 00.000.000/0000-00
    return digits
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  },
  set(value) {
    formData.cpfCnpj = value.replace(/\D/g, '');
  },
});

const formattedPhone = computed({
  get() {
    const value = formData.billingPhone || '';
    const digits = value.replace(/\D/g, '');

    if (digits.length <= 10) {
      // (00) 0000-0000
      return digits
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    // (00) 00000-0000
    return digits
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2');
  },
  set(value) {
    formData.billingPhone = value.replace(/\D/g, '');
  },
});

const formattedPostalCode = computed({
  get() {
    const value = formData.postalCode || '';
    const digits = value.replace(/\D/g, '');
    // 00000-000
    return digits.replace(/(\d{5})(\d)/, '$1-$2');
  },
  set(value) {
    formData.postalCode = value.replace(/\D/g, '');
  },
});

const cpfCnpjLabel = computed(() => {
  const digits = formData.cpfCnpj?.replace(/\D/g, '') || '';
  if (digits.length > 11) {
    return 'CNPJ';
  }
  return 'CPF/CNPJ';
});

const hasChanges = computed(() => {
  return JSON.stringify(formData) !== JSON.stringify(originalData.value);
});

const isCpfCnpjValid = computed(() => {
  const digits = formData.cpfCnpj?.replace(/\D/g, '') || '';
  return digits.length === 11 || digits.length === 14;
});

const isFormValid = computed(() => {
  // CPF/CNPJ is required when Asaas is configured
  if (asaasRequired.value && !isCpfCnpjValid.value) {
    return false;
  }
  // Name is required for fiscal note
  if (!formData.billingName?.trim()) {
    return false;
  }
  return true;
});

// Check if billing data is configured
const isDataConfigured = computed(() => {
  return formData.cpfCnpj && isCpfCnpjValid.value && formData.billingName;
});

// Masked CPF/CNPJ for display (shows only last 4 digits)
const maskedCpfCnpj = computed(() => {
  const digits = formData.cpfCnpj?.replace(/\D/g, '') || '';
  if (!digits) return '';

  if (digits.length === 11) {
    // CPF: ***.***.***-XX
    return `***.***.*${digits.slice(-3, -2)}*-${digits.slice(-2)}`;
  }
  if (digits.length === 14) {
    // CNPJ: **.***.***/**XX-XX
    return `**.***.***/${digits.slice(-6, -4)}**-${digits.slice(-2)}`;
  }
  return '***';
});

// Summary for collapsed state
const summaryInfo = computed(() => {
  if (!formData.billingName && !formData.cpfCnpj) {
    return null;
  }
  return {
    name: formData.billingName || '-',
    document: maskedCpfCnpj.value || '-',
    city:
      formData.city && formData.state
        ? `${formData.city}/${formData.state}`
        : null,
  };
});

// Brazilian states
const states = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapa' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceara' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espirito Santo' },
  { value: 'GO', label: 'Goias' },
  { value: 'MA', label: 'Maranhao' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Para' },
  { value: 'PB', label: 'Paraiba' },
  { value: 'PR', label: 'Parana' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piaui' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondonia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'Sao Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' },
];

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};

const fetchBillingDetails = async () => {
  isLoading.value = true;
  try {
    const { data } = await BillingAPI.getBillingDetails();
    formData.cpfCnpj = data.cpf_cnpj || '';
    formData.billingName = data.billing_name || '';
    formData.billingEmail = data.billing_email || '';
    formData.billingPhone = data.billing_phone || '';

    // Parse address if it exists
    const address = data.billing_address || {};
    formData.postalCode = address.postal_code || '';
    formData.street = address.street || '';
    formData.number = address.number || '';
    formData.complement = address.complement || '';
    formData.neighborhood = address.neighborhood || '';
    formData.city = address.city || '';
    formData.state = address.state || '';

    asaasRequired.value = data.asaas_required || false;

    // Store original data for change detection
    originalData.value = { ...formData };

    // Auto-expand if data is not configured and Asaas is required
    if (asaasRequired.value && !isDataConfigured.value) {
      isExpanded.value = true;
    }
  } catch {
    // Error handling without console
  } finally {
    isLoading.value = false;
  }
};

const fetchAddressByCep = async () => {
  const cep = formData.postalCode?.replace(/\D/g, '');
  if (cep?.length !== 8) return;

  try {
    const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
    const data = await response.json();

    if (!data.erro) {
      formData.street = data.logradouro || formData.street;
      formData.neighborhood = data.bairro || formData.neighborhood;
      formData.city = data.localidade || formData.city;
      formData.state = data.uf || formData.state;
    }
  } catch {
    // Error handling without console
  }
};

// Watch for CEP changes to auto-fill address
watch(
  () => formData.postalCode,
  newValue => {
    if (newValue?.replace(/\D/g, '').length === 8) {
      fetchAddressByCep();
    }
  }
);

const saveBillingDetails = async () => {
  if (!isFormValid.value) {
    useAlert(t('BILLING_SETTINGS.BILLING_DETAILS.INVALID_ERROR'));
    return;
  }

  isSaving.value = true;
  try {
    await BillingAPI.updateBillingDetails({
      cpf_cnpj: formData.cpfCnpj,
      billing_name: formData.billingName,
      billing_email: formData.billingEmail,
      billing_phone: formData.billingPhone,
      billing_address: {
        postal_code: formData.postalCode,
        street: formData.street,
        number: formData.number,
        complement: formData.complement,
        neighborhood: formData.neighborhood,
        city: formData.city,
        state: formData.state,
      },
    });
    originalData.value = { ...formData };
    useAlert(t('BILLING_SETTINGS.BILLING_DETAILS.SUCCESS'));
    emit('updated');
    // Collapse after successful save
    isExpanded.value = false;
  } catch (error) {
    const message =
      error.response?.data?.error ||
      t('BILLING_SETTINGS.BILLING_DETAILS.ERROR');
    useAlert(message);
  } finally {
    isSaving.value = false;
  }
};

onMounted(fetchBillingDetails);
</script>

<template>
  <div class="space-y-4">
    <!-- Header (Always visible, clickable to toggle) -->
    <div
      class="flex items-center justify-between cursor-pointer select-none group"
      @click="toggleExpanded"
    >
      <div class="flex items-start gap-3">
        <div
          class="w-10 h-10 rounded-lg bg-n-blue-2 dark:bg-n-blue-9/20 flex items-center justify-center flex-shrink-0"
        >
          <span class="i-lucide-file-text text-n-blue-11 text-lg" />
        </div>
        <div class="flex-1">
          <div class="flex items-center gap-2">
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ t('BILLING_SETTINGS.BILLING_DETAILS.TITLE') }}
            </h3>
            <!-- Status badge -->
            <span
              v-if="isDataConfigured"
              class="px-2 py-0.5 rounded-full bg-n-teal-9 text-white text-xs font-medium"
            >
              {{ t('BILLING_SETTINGS.BILLING_DETAILS.CONFIGURED') }}
            </span>
            <span
              v-else-if="asaasRequired"
              class="px-2 py-0.5 rounded-full bg-n-amber-9 text-white text-xs font-medium"
            >
              {{ t('BILLING_SETTINGS.BILLING_DETAILS.PENDING') }}
            </span>
          </div>
          <p class="text-sm text-n-slate-10 mt-0.5">
            {{ t('BILLING_SETTINGS.BILLING_DETAILS.DESCRIPTION') }}
          </p>

          <!-- Collapsed Summary -->
          <div
            v-if="!isExpanded && summaryInfo && !isLoading"
            class="mt-2 flex items-center gap-4 text-sm text-n-slate-11"
          >
            <span class="flex items-center gap-1.5">
              <span class="i-lucide-user text-n-slate-9" />
              {{ summaryInfo.name }}
            </span>
            <span class="flex items-center gap-1.5">
              <span class="i-lucide-credit-card text-n-slate-9" />
              {{ summaryInfo.document }}
            </span>
            <span v-if="summaryInfo.city" class="flex items-center gap-1.5">
              <span class="i-lucide-map-pin text-n-slate-9" />
              {{ summaryInfo.city }}
            </span>
          </div>
        </div>
      </div>

      <!-- Toggle icon -->
      <div class="flex items-center gap-2">
        <ButtonV4
          v-if="!isExpanded"
          xs
          faded
          slate
          @click.stop="toggleExpanded"
        >
          {{ t('BILLING_SETTINGS.BILLING_DETAILS.EDIT') }}
        </ButtonV4>
        <span
          class="i-lucide-chevron-down text-xl text-n-slate-10 transition-transform duration-200"
          :class="{ 'rotate-180': isExpanded }"
        />
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <span class="i-lucide-loader-2 animate-spin text-2xl text-n-slate-10" />
    </div>

    <!-- Expandable Form Content -->
    <transition
      enter-active-class="transition-all duration-300 ease-out"
      leave-active-class="transition-all duration-200 ease-in"
      enter-from-class="opacity-0 max-h-0"
      enter-to-class="opacity-100 max-h-[2000px]"
      leave-from-class="opacity-100 max-h-[2000px]"
      leave-to-class="opacity-0 max-h-0"
    >
      <div v-if="isExpanded && !isLoading" class="overflow-hidden">
        <div class="space-y-5 pt-4 border-t border-n-slate-3">
          <!-- Personal/Company Data Section -->
          <div class="space-y-4">
            <h4
              class="text-sm font-medium text-n-slate-11 flex items-center gap-2"
            >
              <span class="i-lucide-user text-base" />
              {{ t('BILLING_SETTINGS.BILLING_DETAILS.PERSONAL_DATA') }}
            </h4>

            <!-- Name Field -->
            <div>
              <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
                {{ t('BILLING_SETTINGS.BILLING_DETAILS.FULL_NAME') }}
                <span class="text-n-red-9">{{
                  t('BILLING_SETTINGS.BILLING_DETAILS.REQUIRED_MARKER')
                }}</span>
              </label>
              <input
                v-model="formData.billingName"
                type="text"
                class="w-full px-3 py-2.5 rounded-lg border border-n-slate-4 bg-white dark:bg-n-slate-2 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500 transition-colors"
                :placeholder="
                  t('BILLING_SETTINGS.BILLING_DETAILS.FULL_NAME_PLACEHOLDER')
                "
              />
            </div>

            <!-- CPF/CNPJ Field -->
            <div>
              <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
                {{ cpfCnpjLabel }}
                <span v-if="asaasRequired" class="text-n-red-9">{{
                  t('BILLING_SETTINGS.BILLING_DETAILS.REQUIRED_MARKER')
                }}</span>
              </label>
              <input
                v-model="formattedCpfCnpj"
                type="text"
                class="w-full px-3 py-2.5 rounded-lg border border-n-slate-4 bg-white dark:bg-n-slate-2 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500 transition-colors"
                :placeholder="t('BILLING_SETTINGS.BILLING_DETAILS.PLACEHOLDER')"
                maxlength="18"
              />
              <p class="text-xs text-n-slate-10 mt-1">
                {{ t('BILLING_SETTINGS.BILLING_DETAILS.HELP_TEXT') }}
              </p>
            </div>

            <!-- Email and Phone Row -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
                  {{ t('BILLING_SETTINGS.BILLING_DETAILS.BILLING_EMAIL') }}
                </label>
                <input
                  v-model="formData.billingEmail"
                  type="email"
                  class="w-full px-3 py-2.5 rounded-lg border border-n-slate-4 bg-white dark:bg-n-slate-2 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500 transition-colors"
                  :placeholder="
                    t('BILLING_SETTINGS.BILLING_DETAILS.EMAIL_PLACEHOLDER')
                  "
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
                  {{ t('BILLING_SETTINGS.BILLING_DETAILS.PHONE') }}
                </label>
                <input
                  v-model="formattedPhone"
                  type="text"
                  class="w-full px-3 py-2.5 rounded-lg border border-n-slate-4 bg-white dark:bg-n-slate-2 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500 transition-colors"
                  :placeholder="
                    t('BILLING_SETTINGS.BILLING_DETAILS.PHONE_PLACEHOLDER')
                  "
                  maxlength="15"
                />
              </div>
            </div>
          </div>

          <!-- Address Section -->
          <div class="space-y-4 pt-2 border-t border-n-slate-3">
            <h4
              class="text-sm font-medium text-n-slate-11 flex items-center gap-2 pt-2"
            >
              <span class="i-lucide-map-pin text-base" />
              {{ t('BILLING_SETTINGS.BILLING_DETAILS.BILLING_ADDRESS') }}
            </h4>

            <!-- CEP Field -->
            <div class="max-w-[200px]">
              <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
                {{ t('BILLING_SETTINGS.BILLING_DETAILS.POSTAL_CODE') }}
              </label>
              <input
                v-model="formattedPostalCode"
                type="text"
                class="w-full px-3 py-2.5 rounded-lg border border-n-slate-4 bg-white dark:bg-n-slate-2 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500 transition-colors"
                :placeholder="
                  t('BILLING_SETTINGS.BILLING_DETAILS.POSTAL_CODE_FORMAT')
                "
                maxlength="9"
              />
              <p class="text-xs text-n-slate-10 mt-1">
                {{ t('BILLING_SETTINGS.BILLING_DETAILS.POSTAL_CODE_HELP') }}
              </p>
            </div>

            <!-- Street and Number Row -->
            <div class="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div class="sm:col-span-3">
                <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
                  {{ t('BILLING_SETTINGS.BILLING_DETAILS.STREET') }}
                </label>
                <input
                  v-model="formData.street"
                  type="text"
                  class="w-full px-3 py-2.5 rounded-lg border border-n-slate-4 bg-white dark:bg-n-slate-2 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500 transition-colors"
                  :placeholder="
                    t('BILLING_SETTINGS.BILLING_DETAILS.STREET_PLACEHOLDER')
                  "
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
                  {{ t('BILLING_SETTINGS.BILLING_DETAILS.NUMBER') }}
                </label>
                <input
                  v-model="formData.number"
                  type="text"
                  class="w-full px-3 py-2.5 rounded-lg border border-n-slate-4 bg-white dark:bg-n-slate-2 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500 transition-colors"
                  :placeholder="
                    t('BILLING_SETTINGS.BILLING_DETAILS.NUMBER_PLACEHOLDER')
                  "
                />
              </div>
            </div>

            <!-- Complement and Neighborhood Row -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
                  {{ t('BILLING_SETTINGS.BILLING_DETAILS.COMPLEMENT') }}
                </label>
                <input
                  v-model="formData.complement"
                  type="text"
                  class="w-full px-3 py-2.5 rounded-lg border border-n-slate-4 bg-white dark:bg-n-slate-2 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500 transition-colors"
                  :placeholder="
                    t('BILLING_SETTINGS.BILLING_DETAILS.COMPLEMENT_PLACEHOLDER')
                  "
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
                  {{ t('BILLING_SETTINGS.BILLING_DETAILS.NEIGHBORHOOD') }}
                </label>
                <input
                  v-model="formData.neighborhood"
                  type="text"
                  class="w-full px-3 py-2.5 rounded-lg border border-n-slate-4 bg-white dark:bg-n-slate-2 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500 transition-colors"
                  :placeholder="
                    t(
                      'BILLING_SETTINGS.BILLING_DETAILS.NEIGHBORHOOD_PLACEHOLDER'
                    )
                  "
                />
              </div>
            </div>

            <!-- City and State Row -->
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div class="sm:col-span-2">
                <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
                  {{ t('BILLING_SETTINGS.BILLING_DETAILS.CITY') }}
                </label>
                <input
                  v-model="formData.city"
                  type="text"
                  class="w-full px-3 py-2.5 rounded-lg border border-n-slate-4 bg-white dark:bg-n-slate-2 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500 transition-colors"
                  :placeholder="
                    t('BILLING_SETTINGS.BILLING_DETAILS.CITY_PLACEHOLDER')
                  "
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
                  {{ t('BILLING_SETTINGS.BILLING_DETAILS.STATE') }}
                </label>
                <select
                  v-model="formData.state"
                  class="w-full px-3 py-2.5 rounded-lg border border-n-slate-4 bg-white dark:bg-n-slate-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500 transition-colors"
                >
                  <option value="">
                    {{ t('BILLING_SETTINGS.BILLING_DETAILS.SELECT_STATE') }}
                  </option>
                  <option
                    v-for="state in states"
                    :key="state.value"
                    :value="state.value"
                  >
                    {{ state.label }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <!-- Info Alert when CPF/CNPJ is missing and Asaas is configured -->
          <div
            v-if="asaasRequired && !formData.cpfCnpj"
            class="p-3 rounded-lg bg-n-amber-2 border border-n-amber-6"
          >
            <div class="flex items-start gap-2">
              <span
                class="i-lucide-alert-triangle text-n-amber-11 text-lg flex-shrink-0 mt-0.5"
              />
              <div>
                <p class="text-sm font-medium text-n-amber-12">
                  {{ t('BILLING_SETTINGS.BILLING_DETAILS.MISSING_TITLE') }}
                </p>
                <p class="text-sm text-n-amber-11 mt-0.5">
                  {{ t('BILLING_SETTINGS.BILLING_DETAILS.MISSING_DESC') }}
                </p>
              </div>
            </div>
          </div>

          <!-- Success indicator when data is complete -->
          <div
            v-else-if="
              formData.cpfCnpj && isCpfCnpjValid && formData.billingName
            "
            class="p-3 rounded-lg bg-n-teal-2 border border-n-teal-6"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-check-circle text-n-teal-11 text-lg" />
              <p class="text-sm text-n-teal-11">
                {{ t('BILLING_SETTINGS.BILLING_DETAILS.DATA_CONFIGURED') }}
              </p>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex justify-end gap-2 pt-2">
            <ButtonV4 sm faded slate @click="isExpanded = false">
              {{ t('BILLING_SETTINGS.BILLING_DETAILS.CANCEL') }}
            </ButtonV4>
            <ButtonV4
              sm
              solid
              blue
              :is-loading="isSaving"
              :disabled="!hasChanges || !isFormValid"
              @click="saveBillingDetails"
            >
              {{ t('BILLING_SETTINGS.BILLING_DETAILS.SAVE') }}
            </ButtonV4>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
